<template>
    <search-layout
        v-if="loaded"
        :map-expanded="mapExpanded"
        :offers-count="filteredOffers.length"
        :search-active="searchActive"
    >
        <template v-slot:breadcrumbs>
            <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="search" />
            <cruises-breadcrumbs :search-request="searchRequest" />
        </template>
        <template v-slot:left-sidebar>
            <cruises-search-summary :dates="dates" :search-request="searchRequest" />
            <sort-select v-model="sortKey" :items="sortItems" />
            <group-offers-select
                class="pt-3"
                :items="['none', 'serviceName', 'mealTypeName', 'startDate']"
                :product-store="productStore"
                token-base="cruiseGroupBy"
            />
            <cruises-filters
                :filter-values="filterValues"
                :filters="searchResponse.filters"
                :filtered-offers-count="filteredOffers.length"
                :currency="currency"
                @change="changeFilter"
                @reset="resetFilters"
            />
        </template>
        <template v-slot:main-content>
            <search-progress
                :filtered-offers-count="filteredOffers.length"
                :total-offers-count="searchResponse.products.length"
                :search-active="searchActive"
                @stop="stopSearch"
            />
            <cruises-offers :offers="offers" />
            <pagination v-model="page" :pages="pages" />
            <conditions-modal>
                <template v-slot:info>
                    <cruise-conditions-modal-info />
                </template>
            </conditions-modal>
            <cruise-offer-info-modal />
        </template>
        <template v-slot:right-sidebar>
            <client-only>
                <cruise-map v-model="mapExpanded" :points="mapPoints" />
            </client-only>
        </template>
    </search-layout>
</template>

<script lang="ts">
    import {Component, mixins, Watch} from 'vue-facing-decorator'
    import MobileSearchPageTabs from '@/components/snippets/MobileSearchPageTabs.vue'
    import Pagination from '~/components/search/Pagination.vue'
    import ConditionsModal from '~/components/modals/ConditionsModal.vue'
    import {SearchPageBase} from '@/mixins/SearchPageBase.vue'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import SearchProgress from '~src/components/search/searchProgress.src'
    import SortSelect from '@/components/search/SortSelect.vue'
    import GroupOffersSelect from '@/components/search/GroupOffersSelect.vue'
    import SearchLayout from '~src/components/parts/searchLayout.src'
    import {SearchPageMixin} from '@/components/mixins/SearchPageMixin.vue'
    import _Tmp_SearchPageBase, {Tmp_SearchPageBase} from '@/mixins/Tmp_SearchPageBase.vue'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import CruisesSearchSummary from '@/components/cruises/search/CruisesSearchSummary.vue'
    import CruisesOffers from '@/components/cruises/search/offers/CruiseOffers.vue'
    import CruisesBreadcrumbs from '@/components/cruises/CruisesBreadcrumbs.vue'
    import {CruisesStoreMixin} from '@/mixins/CruisesStoreMixin.vue'
    import {CruisesPageMixin} from '~src/components/cruises/mixins/CruisesPageMixin.vue'
    import CruisesFilters from '@/components/cruises/search/CruisesFilters.vue'
    import CruiseConditionsModalInfo from '@/components/cruises/snippets/CruiseConditionsModalInfo.vue'
    import CruiseMap from '@/components/cruises/search/CruiseMap.vue'
    import CruiseOfferInfoModal from '@/components/cruises/info/CruiseOfferInfoModal.vue'
    import {useCruiseRuntimeData} from '~/composables/cruise'
    import {callComponentComputed} from '~/utils/vue-class-helpers'

    @Component({
        components: {
            CruiseOfferInfoModal,
            CruiseMap,
            CruiseConditionsModalInfo,
            CruisesFilters,
            CruisesBreadcrumbs,
            CruisesOffers,
            CruisesSearchSummary,
            Breadcrumbs,
            SortSelect,
            SearchProgress,
            ExpiredOffersSnackbar,
            Pagination,
            MobileSearchPageTabs,
            ConditionsModal,
            SearchLayout,
            GroupOffersSelect,
        },
        async setup() {
            const route = useRoute()
            const {departureCountryId, departureCityId, destinationCountryId, destinationCityId} = route.query
            await useAsyncData(
                `cruise-${departureCountryId}-${departureCityId}-${destinationCountryId}-${destinationCityId}`,
                async () => {
                    await useCruiseRuntimeData(route)
                    return true
                }
            )
        },
    })
    export class CruisesSearchPage extends mixins(
        SearchPageBase,
        Tmp_SearchPageBase,
        SearchPageMixin,
        CruisesStoreMixin,
        CruisesPageMixin
    ) {
        mapPoints = []

        @Watch('filteredOffers', {deep: true, immediate: true})
        async onOffersChange() {
            await this.loadMapPoints()
        }

        async loadMapPoints() {
            const loadCity = async location => {
                try {
                    return (
                        await this.$api.locations.get({
                            id: location.cityId,
                            countryId: location.countryId,
                            limitCities: 1,
                        })
                    )?.cities?.[0]
                } catch (e) {
                    this.$toast.error(this.$t('error_message.load_error'))
                }
            }

            const departureObjCities = this.filteredOffers.reduce((arr, product) => {
                if (
                    !arr.find(
                        el =>
                            el.cityId === product.departurePoint?.cityId &&
                            el.countryId === product.departurePoint?.countryId
                    )
                ) {
                    arr.push(product.departurePoint)
                }

                return arr
            }, [])

            const destinationObjCities = this.filteredOffers.reduce((arr, product) => {
                product.destinationPoints.forEach(location => {
                    if (!arr.find(el => el.cityId === location.cityId && el.countryId === location.countryId)) {
                        arr.push(location)
                    }
                })

                return arr
            }, [])

            const uniqueCities = [...departureObjCities, ...destinationObjCities].reduce((arr, location) => {
                if (!arr.find(el => el.cityId === location.cityId && el.countryId === location.countryId)) {
                    arr.push(location)
                }

                return arr
            }, [])

            this.mapPoints = await Promise.all(uniqueCities.map(city => loadCity(city)))
        }

        get dates() {
            return `${this.formattedDate(this.searchRequest.startDate)} - ${this.formattedDate(
                this.searchRequest.endDate
            )}`
        }

        formattedDate(date) {
            return date ? this.$filters.dateFormat(this.$dateFns.parseISO(date)) : ''
        }

        get sortItems() {
            return [
                ...callComponentComputed(this, _Tmp_SearchPageBase, 'sortItems'),
                {
                    text: this.$t('sort.date_asc'),
                    value: 'dateAsc',
                },
            ]
        }
    }

    export default CruisesSearchPage
</script>
